import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { constants } from "../../constants/constants";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { createPopup, updatePopupData, deletePopupData, cleanupPopupImages } from "../../utils/ApiCalls/Popup";
import { addlog } from "../../utils/ApiCalls/Logs";
import { LOG_ACTIONS } from "../../utils/Constant/LogConstant";
import { uploadFileMobile } from "../../utils/ApiCalls/Document";
import { IMAGE_TYPE } from "../../constants/ImageConstant";
import UploadFileImage from "../imageUpload/uploadFileImage";
import LocationAndExpcept from "../../constants/LocationAndExpcept";
import { getAllLocations } from "../../utils/ApiCalls/Locations";
import DateAndTimePicker from "../DateAndTimePicker/notificationDateTimePicker";
import moment from "moment-timezone";
import { getTimeData } from "../../constants/getTimeDate";
import AltTextInput, { pickAltText } from "../AltTextInput/AltTextInput";

const POPUP_ALT_FIELDS = [
  "english_image_alt",
  "traditional_chinese_image_alt",
  "simplified_chinese_image_alt",
  "bahasa_image_alt",
];

const PopupInput = ({ popup, relatedPopups = [] }) => {
  const [englishFile, setEnglishFile] = useState(null);
  const [traditionalChineseFile, setTraditionalChineseFile] = useState(null);
  const [simplifiedChineseFile, setSimplifiedChineseFile] = useState(null);
  const [bahasaFile, setBahasaFile] = useState(null);

  const [englishUrl, setEnglishUrl] = useState("");
  const [traditionalChineseUrl, setTraditionalChineseUrl] = useState("");
  const [simplifiedChineseUrl, setSimplifiedChineseUrl] = useState("");
  const [bahasaUrl, setBahasaUrl] = useState("");
  const [altText, setAltText] = useState({});

  const [displayLocation, setDisplayLocation] = useState([]);
  const [locationOption, setLocationOption] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);

  const [urlErrors, setUrlErrors] = useState({ en: "", tc: "", sc: "", bm: "" });
  const [dateErrors, setDateErrors] = useState({ validFrom: "", validTo: "" });

  const [{ userDetails }, dispatch] = useDataLayerValue();
  const navigate = useNavigate();

  useEffect(() => {
    getOptionLocation();
  }, []);

  const getOptionLocation = async () => {
    const data = await getAllLocations();
    if (!data.error) setLocationOption(data?.data || []);
  };

  const handleDisplayLocationChange = (selectedOptions) => {
    setDisplayLocation(Array.isArray(selectedOptions) ? selectedOptions : selectedOptions?.target?.value ?? []);
  };

  const isValidUrl = (url) => {
    try { return Boolean(new URL(url)); } catch { return false; }
  };

  useEffect(() => {
    if (popup?.id) {
      setEnglishFile(popup.english_image || null);
      setTraditionalChineseFile(popup.traditional_chinese_image || null);
      setSimplifiedChineseFile(popup.simplified_chinese_image || null);
      setBahasaFile(popup.bahasa_image || null);

      setEnglishUrl(popup.english_url || "");
      setTraditionalChineseUrl(popup.traditional_chinese_url || "");
      setSimplifiedChineseUrl(popup.simplified_chinese_url || "");
      setBahasaUrl(popup.bahasa_url || "");
      setAltText(pickAltText(popup, POPUP_ALT_FIELDS));

      // Pre-select locations from all related popups in the same group
      const allLocations = relatedPopups.length > 0
        ? relatedPopups.map((p) => parseInt(p.location)).filter(Boolean)
        : popup.location
          ? popup.location.split(",").map((r) => parseInt(r.trim())).filter(Boolean)
          : [];
      setDisplayLocation([...new Set(allLocations)]);

      setIsActive(popup.is_active === 1);
      setValidFrom(popup.valid_from || null);
      setValidTo(popup.valid_to || null);
    }
  }, [popup, relatedPopups]);

  const uploadImage = async (file, label, oldFileName = null) => {
    if (!file) return { url: "" };
    if (typeof file === "string") {
      return { url: file.replace(constants.MEDIAURL + "/", "") };
    }
    const result = await uploadFileMobile(file, { type: IMAGE_TYPE.POPUP_IMAGE, subType: "" }, undefined, true, oldFileName);
    if (result?.error) return { error: result.error, label };
    return { url: result?.url || "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!englishFile) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "English image is required." });
    }
    if (!traditionalChineseFile) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "Traditional Chinese image is required." });
    }
    if (!simplifiedChineseFile) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "Simplified Chinese image is required." });
    }
    if (!bahasaFile) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "Bahasa image is required." });
    }
    const newUrlErrors = {
      en: !englishUrl ? "English URL is required" : isValidUrl(englishUrl) ? "" : "Invalid URL",
      tc: !traditionalChineseUrl ? "Traditional Chinese URL is required" : isValidUrl(traditionalChineseUrl) ? "" : "Invalid URL",
      sc: !simplifiedChineseUrl ? "Simplified Chinese URL is required" : isValidUrl(simplifiedChineseUrl) ? "" : "Invalid URL",
      bm: !bahasaUrl ? "Bahasa URL is required" : isValidUrl(bahasaUrl) ? "" : "Invalid URL",
    };
    setUrlErrors(newUrlErrors);
    if (Object.values(newUrlErrors).some(Boolean)) return;

    const newDateErrors = {
      validFrom: !validFrom ? "Valid From is required" : "",
      validTo: !validTo ? "Valid To is required" : "",
    };
    setDateErrors(newDateErrors);
    if (Object.values(newDateErrors).some(Boolean)) return;

    if (displayLocation.length === 0) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "Please select at least one location." });
    }

    dispatch({ type: "SET_LOADING", loading: true });

    try {
      const [enResult, tcResult, scResult, bmResult] = await Promise.all([
        uploadImage(
          englishFile,
          "English Popup Image",
          typeof popup?.english_image === "string"
            ? popup.english_image.replace(constants.MEDIAURL + "/", "").replace(constants.MEDIAURL, "")
            : null
        ),
        uploadImage(
          traditionalChineseFile,
          "Traditional Chinese Popup Image",
          typeof popup?.traditional_chinese_image === "string"
            ? popup.traditional_chinese_image.replace(constants.MEDIAURL + "/", "").replace(constants.MEDIAURL, "")
            : null
        ),
        uploadImage(
          simplifiedChineseFile,
          "Simplified Chinese Popup Image",
          typeof popup?.simplified_chinese_image === "string"
            ? popup.simplified_chinese_image.replace(constants.MEDIAURL + "/", "").replace(constants.MEDIAURL, "")
            : null
        ),
        uploadImage(
          bahasaFile,
          "Bahasa Popup Image",
          typeof popup?.bahasa_image === "string"
            ? popup.bahasa_image.replace(constants.MEDIAURL + "/", "").replace(constants.MEDIAURL, "")
            : null
        ),
      ]);

      const uploadFailed = [enResult, tcResult, scResult, bmResult].find((r) => r?.error);
      if (uploadFailed) {
        return Swal.fire({ icon: "error", title: uploadFailed.label, text: uploadFailed.error });
      }

      const [enImg, tcImg, scImg, bmImg] = [enResult, tcResult, scResult, bmResult].map((r) => r.url);

      const basePayload = {
        english_image: enImg,
        traditional_chinese_image: tcImg,
        simplified_chinese_image: scImg,
        bahasa_image: bmImg,
        english_url: englishUrl || null,
        traditional_chinese_url: traditionalChineseUrl || null,
        simplified_chinese_url: simplifiedChineseUrl || null,
        bahasa_url: bahasaUrl || null,
        is_active: isActive ? 1 : 0,
        ...altText,
      };

      const convertDate = (date, timezone) => {
        if (!date) return null;
        const tz = timezone || "Asia/Kuala_Lumpur";
        return moment(getTimeData(date, tz)).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
      };

      let data;
      if (popup?.id) {
        // Existing location IDs from all related popups
        const existingLocIds = relatedPopups.length > 0
          ? relatedPopups.map((p) => parseInt(p.location)).filter(Boolean)
          : [parseInt(popup.location)];

        const allRelated = relatedPopups.length > 0 ? relatedPopups : [popup];

        // Compute, from the whole group's current state, which old image
        // files this save will orphan — an old value held by any location
        // in the group that won't match the new value being applied
        // everywhere. Sent once to the backend after all the location
        // saves below finish, so it doesn't depend on their ordering.
        const POPUP_IMAGE_FIELDS = ["english_image", "traditional_chinese_image", "simplified_chinese_image", "bahasa_image"];
        const oldImageValues = new Set();
        allRelated.forEach((p) => {
          POPUP_IMAGE_FIELDS.forEach((field) => {
            if (p[field]) oldImageValues.add(p[field]);
          });
        });
        const imagesToDelete = Array.from(oldImageValues).filter(
          (file) => !POPUP_IMAGE_FIELDS.some((field) => basePayload[field] === file)
        );

        // Delete records for deselected locations
        const toDelete = allRelated.filter((p) => !displayLocation.includes(parseInt(p.location)));
        await Promise.all(toDelete.map((p) => deletePopupData(p.id)));

        // Update records for locations still selected
        const toUpdate = allRelated.filter((p) => displayLocation.includes(parseInt(p.location)));
        const updateResults = await Promise.all(
          toUpdate.map((p) => {
            const loc = locationOption.find((l) => l.id === parseInt(p.location));
            return updatePopupData(p.id, {
              ...basePayload,
              location: p.location,
              valid_from: convertDate(validFrom, loc?.timezone),
              valid_to: convertDate(validTo, loc?.timezone),
            });
          })
        );
        data = updateResults[0];
        const updateFailed = updateResults.find((r) => r?.error);
        if (updateFailed) {
          return Swal.fire({ icon: "error", title: "Oops...", text: updateFailed.error });
        }

        // Create new records for any newly added locations
        const newLocations = displayLocation.filter((id) => !existingLocIds.includes(id));
        if (newLocations.length > 0) {
          const newResults = await Promise.all(
            newLocations.map((locationId) => {
              const loc = locationOption.find((l) => l.id === locationId);
              return createPopup({
                ...basePayload,
                location: String(locationId),
                valid_from: convertDate(validFrom, loc?.timezone),
                valid_to: convertDate(validTo, loc?.timezone),
              });
            })
          );
          const failed = newResults.find((r) => r?.error);
          if (failed) {
            return Swal.fire({ icon: "error", title: "Oops...", text: failed.error });
          }
        }

        await cleanupPopupImages(imagesToDelete);
      } else {
        const results = await Promise.all(
          displayLocation.map((locationId) => {
            const loc = locationOption.find((l) => l.id === locationId);
            return createPopup({
              ...basePayload,
              location: String(locationId),
              valid_from: convertDate(validFrom, loc?.timezone),
              valid_to: convertDate(validTo, loc?.timezone),
            });
          })
        );
        const failed = results.find((r) => r?.error);
        if (failed) {
          return Swal.fire({ icon: "error", title: "Oops...", text: failed.error });
        }
        data = results[0];
      }

      addlog(
        popup?.id ? LOG_ACTIONS.UPDATED : LOG_ACTIONS.CREATED,
        "Popup",
        { id: data?.id || popup?.id },
        userDetails.id
      );

      await Swal.fire({ icon: "success", title: popup?.id ? "Popup updated" : "Popup added", timer: 1500, showConfirmButton: false });
      navigate("/website/webpages/popup");
    } catch (err) {
      Swal.fire({ icon: "error", title: "Oops...", text: err.message || "Unknown error occurred" });
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  const imageUrl = (path) =>
    path ? `${constants.MEDIAURL}/${path}` : undefined;

  return (
    <div>
      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <form className="input_form" onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">

                  <UploadFileImage
                    className="col-lg-3"
                    mandatory="*"
                    width={576}
                    height={743}
                    label="English Popup Image"
                    placeholder="Upload English Popup Image"
                    image={typeof englishFile === "string" ? imageUrl(englishFile) : undefined}
                    selectedFile={typeof englishFile !== "string" ? englishFile : null}
                    setSelectedFile={setEnglishFile}
                  />
                  <UploadFileImage
                    className="col-lg-3"
                    mandatory="*"
                    width={576}
                    height={743}
                    label="Traditional Chinese Popup Image"
                    placeholder="Upload TC Popup Image"
                    image={typeof traditionalChineseFile === "string" ? imageUrl(traditionalChineseFile) : undefined}
                    selectedFile={typeof traditionalChineseFile !== "string" ? traditionalChineseFile : null}
                    setSelectedFile={setTraditionalChineseFile}
                  />
                  <UploadFileImage
                    className="col-lg-3"
                    mandatory="*"
                    width={576}
                    height={743}
                    label="Simplified Chinese Popup Image"
                    placeholder="Upload SC Popup Image"
                    image={typeof simplifiedChineseFile === "string" ? imageUrl(simplifiedChineseFile) : undefined}
                    selectedFile={typeof simplifiedChineseFile !== "string" ? simplifiedChineseFile : null}
                    setSelectedFile={setSimplifiedChineseFile}
                  />
                  <UploadFileImage
                    className="col-lg-3"
                    mandatory="*"
                    width={576}
                    height={743}
                    label="Bahasa Popup Image"
                    placeholder="Upload BM Popup Image"
                    image={typeof bahasaFile === "string" ? imageUrl(bahasaFile) : undefined}
                    selectedFile={typeof bahasaFile !== "string" ? bahasaFile : null}
                    setSelectedFile={setBahasaFile}
                  />
                  <AltTextInput className="col-lg-3" label="English Popup Alt Text" field="english_image_alt" values={altText} setValues={setAltText} />
                  <AltTextInput className="col-lg-3" label="Traditional Chinese Popup Alt Text" field="traditional_chinese_image_alt" values={altText} setValues={setAltText} />
                  <AltTextInput className="col-lg-3" label="Simplified Chinese Popup Alt Text" field="simplified_chinese_image_alt" values={altText} setValues={setAltText} />
                  <AltTextInput className="col-lg-3" label="Bahasa Popup Alt Text" field="bahasa_image_alt" values={altText} setValues={setAltText} />

                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">English URL <span style={{ color: "red" }}>*</span></label>
                      <input
                        className="f_input"
                        type="url"
                        placeholder="https://..."
                        value={englishUrl}
                        onChange={(e) => { setEnglishUrl(e.target.value); setUrlErrors((p) => ({ ...p, en: "" })); }}
                      />
                      {urlErrors.en && <span style={{ color: "red", fontSize: "12px" }}>{urlErrors.en}</span>}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">Traditional Chinese URL <span style={{ color: "red" }}>*</span></label>
                      <input
                        className="f_input"
                        type="url"
                        placeholder="https://..."
                        value={traditionalChineseUrl}
                        onChange={(e) => { setTraditionalChineseUrl(e.target.value); setUrlErrors((p) => ({ ...p, tc: "" })); }}
                      />
                      {urlErrors.tc && <span style={{ color: "red", fontSize: "12px" }}>{urlErrors.tc}</span>}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">Simplified Chinese URL <span style={{ color: "red" }}>*</span></label>
                      <input
                        className="f_input"
                        type="url"
                        placeholder="https://..."
                        value={simplifiedChineseUrl}
                        onChange={(e) => { setSimplifiedChineseUrl(e.target.value); setUrlErrors((p) => ({ ...p, sc: "" })); }}
                      />
                      {urlErrors.sc && <span style={{ color: "red", fontSize: "12px" }}>{urlErrors.sc}</span>}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">Bahasa URL <span style={{ color: "red" }}>*</span></label>
                      <input
                        className="f_input"
                        type="url"
                        placeholder="https://..."
                        value={bahasaUrl}
                        onChange={(e) => { setBahasaUrl(e.target.value); setUrlErrors((p) => ({ ...p, bm: "" })); }}
                      />
                      {urlErrors.bm && <span style={{ color: "red", fontSize: "12px" }}>{urlErrors.bm}</span>}
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">Valid From <span style={{ color: "red" }}>*</span></label>
                      <DateAndTimePicker
                        value={validFrom}
                        setValue={(val) => { setValidFrom(val || null); setDateErrors((p) => ({ ...p, validFrom: "" })); }}
                        min={moment().format("YYYY-MM-DDTHH:mm")}
                        max={validTo}
                      />
                      {dateErrors.validFrom && <span style={{ color: "red", fontSize: "12px" }}>{dateErrors.validFrom}</span>}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">Valid To <span style={{ color: "red" }}>*</span></label>
                      <DateAndTimePicker
                        value={validTo}
                        setValue={(val) => { setValidTo(val || null); setDateErrors((p) => ({ ...p, validTo: "" })); }}
                        min={validFrom || moment().format("YYYY-MM-DDTHH:mm")}
                      />
                      {dateErrors.validTo && <span style={{ color: "red", fontSize: "12px" }}>{dateErrors.validTo}</span>}
                    </div>
                  </div>


                  <div className="col-lg-12">
                    <LocationAndExpcept
                      nondisplay
                      displayLocation={displayLocation}
                      locationOption={locationOption}
                      options={locationOption}
                      setDisplayLocation={setDisplayLocation}
                      handleDisplayLocationChange={handleDisplayLocationChange}
                    />
                  </div>

                  {/* ── Active Toggle ── */}
                  <div className="col-12">
                    <div className="iti_inner">
                      <div className="icheck-primary d-inline">
                        <input
                          type="checkbox"
                          id="popupActiveToggle"
                          name="is_active"
                          checked={isActive}
                          onChange={() => setIsActive((v) => !v)}
                        />
                        <label htmlFor="popupActiveToggle">
                          <p>
                            <span style={{ fontWeight: isActive ? 600 : 200 }}>Active</span>
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              <div className="card-footer text-right">
                <button className="btn main_submit">
                  {popup?.id ? "Update" : "Add"} Popup
                </button>
              </div>
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default PopupInput;
